import { isAcornRequest } from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import { combineReducers } from 'redux';

import airports, * as fromAirports from './airports';
import airportsStats, * as fromAirportsStats from './airportsStats';
import alliances, * as fromAlliances from './alliances';
import { createAlliancesReducer } from './alliances';
import baggage, * as fromBaggage from './baggage';
import { createBaggageReducer } from './baggage';
import carriers, * as fromCarriers from './carriers';
import { createCarriersReducer } from './carriers';
import departureTimes, * as fromDepartureTimes from './departureTimes';
import { createDepartureTimesReducer } from './departureTimes';
import duration, * as fromDuration from './duration';
import { createDurationReducer } from './duration';
import eco, * as fromEco from './eco';
import { createEcoReducer } from './eco';
import filterOrSortChangedCount from './filterOrSortChangedCount';
import hotelsFilter, * as fromHotelsFilter from './hotelsFilter';
import * as fromLayovers from './layovers';
import { createLayoversReducer } from './layovers';
import mixedAirport, * as fromMixedAirport from './mixedAirport';
import stops, * as fromStops from './stops';
import { createStopsReducer } from './stops';
import {
  getAlliancesFromUrl,
  getBaggageFromUrl,
  getCarriersFromUrl,
  getDepartureTimesFromUrl,
  getDurationFromUrl,
  getEcoFromUrl,
  getLayoversFromUrl,
  getStopsFromUrl,
} from './urlInitializers';

import type { AirportsStats } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/airportsStats';
import type { AlliancesFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/alliances';
import type { BaggageFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/baggage';
import type { CarriersFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';
import type { DepartureTimesState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/departureTimes';
import type { DurationFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/duration';
import type { EcoFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/eco';
import type { SelectedHotelsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/hotelsFilter';
import type { MixedAirportFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/mixedAirport';
import type { FiltersState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/state';
import type { StopsFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/stops';

export default isAcornRequest()
  ? combineReducers({
      filterOrSortChangedCount,
      layovers: createLayoversReducer(getLayoversFromUrl),
      airports,
      airportsStats,
      stops: createStopsReducer(getStopsFromUrl),
      departureTimes: createDepartureTimesReducer(getDepartureTimesFromUrl),
      duration: createDurationReducer(getDurationFromUrl),
      carriers: createCarriersReducer(getCarriersFromUrl, { multipleFollowsInferDefault: true }),
      alliances: createAlliancesReducer(getAlliancesFromUrl),
      mixedAirport,
      hotelsFilter,
      eco: createEcoReducer(getEcoFromUrl),
      baggage: createBaggageReducer(getBaggageFromUrl),
    })
  : combineReducers({
      filterOrSortChangedCount,
      layovers: createLayoversReducer(getLayoversFromUrl),
      airports,
      airportsStats,
      stops,
      departureTimes,
      duration,
      carriers,
      alliances,
      mixedAirport,
      hotelsFilter,
      eco,
      baggage,
    });

export const getAirportsFilter = (state: FiltersState) => fromAirports.get(state.airports);
export const getAirportsStatsFilter = (state: FiltersState): AirportsStats =>
  fromAirportsStats.get(state.airportsStats);
export const getFilterOrSortChangedCount = (state: FiltersState) => state.filterOrSortChangedCount;
export const getLayoversDurationFilter = (state: FiltersState) =>
  fromLayovers.getDuration(state.layovers);
export const getLayoversExcludedAirportsFilter = (state: FiltersState) =>
  fromLayovers.getExcludedAirports(state.layovers);

export const getStopsFilter = (state: FiltersState): StopsFilterState => fromStops.get(state.stops);
export const getDepartureTimesFilter = (state: FiltersState): DepartureTimesState =>
  fromDepartureTimes.get(state.departureTimes);
export const getDurationFilter = (state: FiltersState): DurationFilterState =>
  fromDuration.get(state.duration);
export const getCarriersFilter = (state: FiltersState): CarriersFilterState =>
  fromCarriers.get(state.carriers);
export const getAlliancesFilter = (state: FiltersState): AlliancesFilterState =>
  fromAlliances.get(state.alliances);
export const getMixedAirportFilter = (state: FiltersState): MixedAirportFilterState =>
  fromMixedAirport.get(state.mixedAirport);
export const getHotelsFilter = (state: FiltersState): SelectedHotelsFilterState =>
  fromHotelsFilter.get(state.hotelsFilter);
export const getEcoFilter = (state: FiltersState): EcoFilterState => fromEco.get(state.eco);
export const getBaggageFilter = (state: FiltersState): BaggageFilterState =>
  fromBaggage.get(state.baggage);
